import type { SxProps, Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { brandPrimary, brandPrimaryHover, brandPrimaryMuted } from "./tokens";
import { themeVar } from "./cssVar";

export const formPrimary = brandPrimary;
export const formPrimaryHover = brandPrimaryHover;
export const formPrimaryMuted = brandPrimaryMuted;

export const formControlHeight = 48;
export const AUTH_FORM_COMPACT_CONTROL_HEIGHT = 42;

export const formMenuItem = {
	hoverBg: brandPrimaryMuted,
	hoverColor: "#1e293b",
	selectedBg: brandPrimary,
	selectedColor: "#ffffff",
	selectedHoverBg: brandPrimaryHover,
} as const;

export const formMenuItemHeight = 40;

/** MUI Radio — unchecked uses muted primary; checked uses brand primary. */
export const formRadioSx: SxProps<Theme> = {
	color: `color-mix(in srgb, ${themeVar.primary} 60%, transparent)`,
	"&.Mui-checked": { color: themeVar.primary },
};

export const formMenuItemLayoutSx: SystemStyleObject<Theme> = {
	minHeight: formMenuItemHeight,
	padding: "10px 12px",
	boxSizing: "border-box",
};

const borderError = themeVar.danger;

export const formFieldPlaceholderColor = "#9CA3AF";
export const formFieldSelectedColor = "#111827";
export const formSelectPlaceholderClassName = "admin-select-placeholder";
export const formSelectValueClassName = "admin-select-value";

/** External field labels — matches institute / employer portal (dark foreground, not brand green). */
export const formFieldLabelClassName = "mb-2.5 block font-medium text-sm sm:text-base text-foreground";

export const formFieldRequiredMarkClassName = "text-red-500";

/** Shared shell for rich text / custom controls — matches outlined input border + radius. */
export const formFieldShellClassName =
	"rounded-[10px] border border-border bg-white transition-colors duration-200 hover:border-[#C4C4C4] focus-within:border-border";

export const formFieldErrorClassName = "mt-1 text-sm text-red-500";

const placeholderColor = formFieldPlaceholderColor;

export const formFieldBg = "#FFF";
export const formFieldBorder = themeVar.border;
export const formFieldBorderWidth = 1.2;
const fieldBorderHover = "#C4C4C4";

export const selectChevron = {
	width: 35,
	height: 36,
	borderRadius: 4,
	rightInset: 5,
} as const;

export const selectChevronCompact = {
	width: 30,
	height: 30,
	borderRadius: 4,
	rightInset: 4,
} as const;

export interface SelectChevronSpec {
	width: number;
	height: number;
	borderRadius: number;
	rightInset: number;
}

export const getSelectChevron = (controlHeight = formControlHeight): SelectChevronSpec =>
	controlHeight <= 44 ? selectChevronCompact : selectChevron;

export const getChevronReserve = (controlHeight = formControlHeight): number => {
	const chevron = getSelectChevron(controlHeight);
	return chevron.width + chevron.rightInset + 8;
};

export const selectChevronCapClassName =
	"absolute z-[2] flex cursor-pointer items-center justify-center border-0 bg-primary p-0 outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

const fieldBorderSx = (error?: boolean): SystemStyleObject<Theme> => ({
	borderColor: error ? borderError : formFieldBorder,
	borderWidth: error ? 1.2 : formFieldBorderWidth,
});

const outlinedFieldBorderStates = (error?: boolean): SystemStyleObject<Theme> => ({
	"& fieldset": fieldBorderSx(error),
	"& .MuiOutlinedInput-notchedOutline": fieldBorderSx(error),
	"&:hover fieldset": {
		borderColor: error ? borderError : fieldBorderHover,
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: error ? borderError : fieldBorderHover,
	},
	"&.Mui-focused fieldset": fieldBorderSx(error),
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": fieldBorderSx(error),
	"&.Mui-error fieldset": {
		borderColor: `${borderError} !important`,
	},
	"&.Mui-error .MuiOutlinedInput-notchedOutline": {
		borderColor: `${borderError} !important`,
	},
});

export const formOutlinedFieldShellSx = (error?: boolean): SystemStyleObject<Theme> => ({
	backgroundColor: `${formFieldBg} !important`,
	borderRadius: "10px",
	...outlinedFieldBorderStates(error),
});

export const outlinedInputExternalLabelProps = {
	notched: false,
} as const;

const selectOutlineNoNotchSx = (error?: boolean): SystemStyleObject<Theme> => ({
	"& fieldset": {
		top: 0,
		...fieldBorderSx(error),
	},
	"& .MuiOutlinedInput-notchedOutline": {
		top: 0,
		...fieldBorderSx(error),
		"& legend": {
			display: "none",
			maxWidth: 0.01,
		},
	},
});

export const selectOutlinedInputSx = (error?: boolean, height = formControlHeight): SystemStyleObject<Theme> => ({
	...formOutlinedFieldShellSx(error),
	...selectOutlineNoNotchSx(error),
	minHeight: height,
	height,
	paddingRight: `${getChevronReserve(height)}px !important`,
	overflow: "visible",
	cursor: "pointer",
});

export const textOutlinedInputSx = (error?: boolean, height = formControlHeight): SystemStyleObject<Theme> => ({
	...formOutlinedFieldShellSx(error),
	minHeight: height,
	height,
	display: "flex",
	alignItems: "center",
	boxSizing: "border-box",
	overflow: "hidden",
});

const selectInnerSx = (height = formControlHeight): SystemStyleObject<Theme> => ({
	padding: height <= 40 ? "8px 8px 8px 12px" : "13px 8px 13px 15px",
	fontSize: height <= 40 ? "13px" : "14px",
	fontWeight: 400,
	lineHeight: 1.5,
	display: "flex",
	alignItems: "center",
	fontFamily: "Poppins, sans-serif",
	minHeight: height,
	height,
	boxSizing: "border-box",
	color: formFieldPlaceholderColor,
	backgroundColor: "transparent",
	cursor: "pointer",
});

export const selectControlSx = (error?: boolean, height = formControlHeight): SxProps<Theme> => {
	const outlined = selectOutlinedInputSx(error, height);
	return {
		"&.MuiOutlinedInput-root": outlined,
		"& .MuiOutlinedInput-root": outlined,
		"& .MuiSelect-select": selectInnerSx(height),
		"& .MuiSelect-icon": {
			display: "none",
		},
		"& .MuiSelect-select.Mui-disabled": {
			WebkitTextFillColor: formFieldSelectedColor,
			color: formFieldSelectedColor,
			opacity: 1,
		},
		"& .MuiSelect-select.Mui-disabled span": {
			color: `${formFieldSelectedColor} !important`,
		},
		[`& .MuiSelect-select .${formSelectPlaceholderClassName}`]: {
			color: `${formFieldPlaceholderColor} !important`,
			WebkitTextFillColor: `${formFieldPlaceholderColor} !important`,
		},
		[`& .MuiSelect-select .${formSelectValueClassName}`]: {
			color: `${formFieldSelectedColor} !important`,
			WebkitTextFillColor: `${formFieldSelectedColor} !important`,
		},
	};
};

const textInputInnerBase: SystemStyleObject<Theme> = {
	fontFamily: "Poppins, sans-serif",
	fontSize: "14px",
	fontWeight: 400,
	color: formFieldSelectedColor,
	boxSizing: "border-box",
	backgroundColor: "transparent",
};

/** Read-only combobox trigger (SelectOrAddDropdown, EquipmentDropdown, etc.). */
export const readOnlyComboboxOutlinedInputSx = (
	options?: { error?: boolean; disabled?: boolean; hasValue?: boolean },
): SystemStyleObject<Theme> => {
	const { error, disabled, hasValue = false } = options ?? {};
	return {
		...selectOutlinedInputSx(error),
		cursor: disabled ? "not-allowed" : "pointer",
		"& .MuiOutlinedInput-input": {
			...textInputInnerBase,
			padding: "13px 8px 13px 15px",
			height: "auto",
			minHeight: "unset",
			cursor: disabled ? "not-allowed" : "pointer",
			color: hasValue ? formFieldSelectedColor : formFieldPlaceholderColor,
			WebkitTextFillColor: hasValue ? formFieldSelectedColor : formFieldPlaceholderColor,
		},
		"&.Mui-disabled": {
			backgroundColor: "#f5f5f5 !important",
			cursor: "not-allowed",
		},
		"&.Mui-disabled .MuiOutlinedInput-input": {
			WebkitTextFillColor: formFieldPlaceholderColor,
			color: formFieldPlaceholderColor,
		},
	};
};

const autofillInputSx = (bg = formFieldBg): SystemStyleObject<Theme> => ({
	"&:-webkit-autofill": {
		WebkitBoxShadow: `0 0 0 1000px ${bg} inset`,
		WebkitTextFillColor: formFieldSelectedColor,
		caretColor: formFieldSelectedColor,
		transition: "background-color 5000s ease-in-out 0s",
	},
	"&:-webkit-autofill:hover": {
		WebkitBoxShadow: `0 0 0 1000px ${bg} inset`,
	},
	"&:-webkit-autofill:focus": {
		WebkitBoxShadow: `0 0 0 1000px ${bg} inset`,
	},
});

export type TextFieldControlSize = number | { height?: string; padding?: string };

const resolveCustomFieldHeight = (height?: string): number => {
	if (!height) return formControlHeight;
	const parsed = parseInt(String(height), 10);
	if (!Number.isFinite(parsed) || parsed < 32) return formControlHeight;
	return parsed;
};

export const textFieldControlSx = (error?: boolean, size?: TextFieldControlSize): SxProps<Theme> => {
	const height =
		typeof size === "object" && size !== null
			? resolveCustomFieldHeight(size.height)
			: typeof size === "number"
				? size
				: formControlHeight;
	const padding = typeof size === "object" && size !== null && size.padding ? size.padding : "13px 15px";

	return {
		"& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": textOutlinedInputSx(error, height),
		"& .MuiOutlinedInput-root:not(.MuiInputBase-multiline) .MuiOutlinedInput-input": {
			...textInputInnerBase,
			...autofillInputSx(formFieldBg),
			padding,
			height: "auto",
			minHeight: "unset",
			maxHeight: "100%",
			lineHeight: "22px",
		},
		"& .MuiOutlinedInput-root.MuiInputBase-multiline": {
			...formOutlinedFieldShellSx(error),
			height: "auto",
			minHeight: height,
			padding,
			alignItems: "flex-start",
		},
		"& .MuiOutlinedInput-root.MuiInputBase-multiline .MuiOutlinedInput-input": {
			...textInputInnerBase,
			padding: 0,
			height: "auto !important",
			minHeight: "unset",
			lineHeight: 1.5,
			overflow: "auto",
			resize: "vertical",
		},
		"& input::placeholder, & textarea::placeholder": {
			color: placeholderColor,
			opacity: 1,
		},
	};
};

/** Compact session/year selector in dashboard headers — same chrome, 40px height. */
export const yearSelectControlHeight = 40;

/** Shared width for session / academic-year dropdowns across dashboards, reports, and filters. */
export const sessionSelectMinWidthPx = 220;
export const sessionSelectMaxWidthPx = 300;
export const sessionSelectClassName = "min-w-[220px] max-w-[300px] shrink-0";

export const yearSelectControlSx = (error?: boolean): SxProps<Theme> =>
	selectControlSx(error, yearSelectControlHeight);

export const mergeSelectSx = (
	error?: boolean,
	height = formControlHeight,
	...overrides: (SxProps<Theme> | undefined)[]
): SxProps<Theme> => [selectControlSx(error, height), ...overrides.filter(Boolean)] as SxProps<Theme>;

export const filterSelectSx = {
	fontFamily: "Poppins, sans-serif",
	backgroundColor: "white",
	borderRadius: "8px",
	"& .MuiOutlinedInput-notchedOutline": {
		borderColor: themeVar.borderStrong,
		borderWidth: "1px",
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: themeVar.primary,
	},
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: themeVar.primary,
		borderWidth: "1px",
	},
} as const;

export const filterMenuItemSx = {
	fontFamily: "Poppins, sans-serif",
	"&:hover": {
		backgroundColor: themeVar.primarySoft,
	},
	"&.Mui-selected": {
		backgroundColor: themeVar.primary,
		borderBottom: "none",
		"&:hover": {
			backgroundColor: themeVar.primaryHover,
		},
	},
} as const;

export const defaultSelectMenuProps = {
	disableAutoFocusItem: true,
	PaperProps: {
		sx: {
			borderRadius: 2,
			boxShadow: "0 2px 8px 0 rgba(16, 24, 40, 0.04)",
			mt: 1,
		},
	},
	sx: {
		zIndex: 10001,
	},
} as const;

export { placeholderColor };
